import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';

// ── DepGuard SVG Logo ──────────────────────────────────────────────────────────
function DepGuardLogo({ size = 28 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M16 2L4 7v8c0 7.18 5.09 13.89 12 15.93C23.91 28.89 28 22.18 28 15V7L16 2z"
        fill="url(#dg-grad)" stroke="url(#dg-stroke)" strokeWidth="0.5"/>
      <path d="M11 16.5l3.5 3.5 6.5-7" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
      <defs>
        <linearGradient id="dg-grad" x1="4" y1="2" x2="28" y2="32" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#6366f1"/>
          <stop offset="100%" stopColor="#4f46e5"/>
        </linearGradient>
        <linearGradient id="dg-stroke" x1="4" y1="2" x2="28" y2="32" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#818cf8"/>
          <stop offset="100%" stopColor="#6366f1"/>
        </linearGradient>
      </defs>
    </svg>
  );
}

// ── Nav structure ──────────────────────────────────────────────────────────────
// user-dependent so it's a function
function getNavGroups(user) {
  return [
    {
      id: 'home',
      label: 'Home',
      icon: '🏠',
      accentColor: '#6366f1',
      items: [
        { to: '/',               icon: '▦',  key: 'nav.dashboard',     exact: true },
        { to: '/command-center', icon: '🎯', key: 'nav.commandCenter' },
      ],
    },
    {
      id: 'repositories',
      label: 'Projects',
      icon: '🗂️',
      accentColor: '#0ea5e9',
      items: [
        { to: '/repos',          icon: '📦', key: 'nav.repositories' },
        { to: '/upload',         icon: '⬆️', key: 'nav.upload' },
        { to: '/packages',       icon: '🔎', key: 'nav.packages' },
        { to: '/impact',         icon: '🌐', key: 'nav.impactGraph' },
        { to: '/matrix',         icon: '📊', key: 'nav.matrix' },
        { to: '/supplier-trust', icon: '🤝', key: 'nav.supplierTrust' },
      ],
    },
    {
      id: 'security',
      label: 'Security',
      icon: '🛡️',
      accentColor: '#ef4444',
      items: [
        { to: '/vigil/scan',         icon: '⚡',  key: 'nav.vigilScan' },
        { to: '/vigil/history',      icon: '🕐',  key: 'nav.vigilHistory' },
        { to: '/vigil/studio',       icon: '🛡️', key: 'nav.vigilStudio' },
        { to: '/vigil/academy',      icon: '🎓',  key: 'nav.vigilAcademy' },
        { to: '/vigil/rules',        icon: '📜',  key: 'nav.vigilRules' },
        { to: '/vigil/ai',           icon: '🤖',  key: 'nav.vigilAI' },
        { to: '/vigil/concepts',     icon: '💡',  key: 'nav.vigilConcepts' },
        { to: '/vigil/traceability', icon: '🗺️', key: 'nav.vigilTraceability' },
        { to: '/security/agent',     icon: '🦾',  key: 'nav.securityAgent' },
      ],
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: '⚙️',
      accentColor: '#94a3b8',
      items: [
        ...(user?.role === 'admin'
          ? [{ to: '/admin/users', icon: '👥', key: 'nav.userManagement' }]
          : []),
        { to: '/ai/settings', icon: '✨', key: 'nav.ai' },
        { to: '/ci-setup',    icon: '🔧', key: 'nav.cisetup' },
        { to: '/webhooks',    icon: '🔔', key: 'nav.webhooks' },
      ],
    },
    {
      id: 'docs',
      label: 'Docs',
      icon: '📚',
      accentColor: '#10b981',
      defaultCollapsed: true,
      items: [
        { to: '/docs',                                icon: '📖', key: 'nav.docs',                    exact: true },
        { to: '/docs/vigil',                          icon: '📄', key: 'nav.vigilDocs',               exact: true },
        { to: '/docs/vigil/tutorial',                 icon: '🎯', key: 'nav.vigilTutorial',           exact: true },
        { to: '/docs/vigil/design',                   icon: '📝', key: 'nav.vigilDesignReport',       exact: true },
        { to: '/docs/intelligence',                   icon: '🧠', key: 'nav.intelligenceDocs',        exact: true },
        { to: '/docs/intelligence/rule-roadmap',      icon: '🗺️', key: 'nav.intelligenceRuleRoadmap', exact: true },
      ],
    },
  ];
}

const STRATEGY_LINKS = [
  { to: '/progress', label: 'Progress' },
  { to: '/roadmap',  label: 'Roadmap' },
  { to: '/plan',     key: 'nav.productPlan' },
  { to: '/pricing',  key: 'nav.pricing' },
];

function readCollapsed(groups) {
  try {
    const stored = JSON.parse(localStorage.getItem('sidebar_collapsed') || '{}');
    // Apply defaultCollapsed for first-time visitors
    groups.forEach(g => {
      if (g.defaultCollapsed && !(g.id in stored)) stored[g.id] = true;
    });
    return stored;
  } catch {
    const defaults = {};
    groups.forEach(g => { if (g.defaultCollapsed) defaults[g.id] = true; });
    return defaults;
  }
}

export default function Sidebar({ setLoading }) {
  const { t } = useTranslation();
  const location = useLocation();
  const { user } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const navGroups = getNavGroups(user);
  const [collapsed, setCollapsed] = useState(() => readCollapsed(navGroups));

  const isActive = (link) =>
    link.exact ? location.pathname === link.to : location.pathname.startsWith(link.to);

  // Auto-expand the group containing the active route
  useEffect(() => {
    navGroups.forEach(group => {
      const hasActive = group.items.some(item => isActive(item));
      if (hasActive && collapsed[group.id]) {
        setCollapsed(prev => {
          const next = { ...prev, [group.id]: false };
          localStorage.setItem('sidebar_collapsed', JSON.stringify(next));
          return next;
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggleGroup = (id) => {
    setCollapsed(prev => {
      const next = { ...prev, [id]: !prev[id] };
      localStorage.setItem('sidebar_collapsed', JSON.stringify(next));
      return next;
    });
  };

  const NavItem = ({ link }) => {
    const active = isActive(link);
    return (
      <Link
        to={link.to}
        className={`ce-nav-item${active ? ' active' : ''}`}
        onClick={() => setMobileOpen(false)}
      >
        <span className="ce-nav-icon">{link.icon}</span>
        <span className="ce-nav-label">{link.key ? t(link.key) : link.label}</span>
        {active && <span className="ce-nav-active-pip" />}
      </Link>
    );
  };

  return (
    <>
      {mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 99, backdropFilter: 'blur(2px)' }}
        />
      )}
      <button
        className="btn btn-sm btn-outline-secondary d-md-none"
        style={{ position: 'fixed', top: 12, left: 12, zIndex: 200 }}
        onClick={() => setMobileOpen(o => !o)}
      >
        ☰
      </button>

      <aside className={`ce-sidebar${mobileOpen ? ' open' : ''}`}>

        {/* ── Logo ── */}
        <Link to="/" className="ce-sidebar-logo" onClick={() => setMobileOpen(false)}>
          <DepGuardLogo size={30} />
          <div>
            <div className="ce-logo-text">DepGuard</div>
            <div className="ce-logo-sub">Dependency Security</div>
          </div>
          <div className="ce-logo-badge">v2</div>
        </Link>

        {/* ── ⌘K search ── */}
        <button
          className="ce-cmdpalette-hint"
          onClick={() => window.dispatchEvent(new CustomEvent('open-cmdpalette'))}
          title="Open command palette (⌘K)"
        >
          <span className="ce-cmdpalette-hint-icon">🔍</span>
          <span className="ce-cmdpalette-hint-text">Search...</span>
          <kbd className="ce-cmdpalette-hint-kbd">⌘K</kbd>
        </button>

        {/* ── Nav ── */}
        <nav className="ce-sidebar-nav">
          {navGroups.map((group, idx) => {
            const isCollapsed = !!collapsed[group.id];
            const hasActive = group.items.some(item => isActive(item));
            return (
              <div key={group.id} className={`ce-nav-group${idx > 0 ? ' ce-nav-group--bordered' : ''}`}>
                <button
                  className={`ce-nav-group-header${hasActive ? ' has-active' : ''}`}
                  onClick={() => toggleGroup(group.id)}
                  aria-expanded={!isCollapsed}
                  style={{ '--group-color': group.accentColor }}
                >
                  <span className="ce-nav-group-icon-wrap" style={{ '--group-color': group.accentColor }}>
                    {group.icon}
                  </span>
                  <span className="ce-nav-group-label">{group.label}</span>
                  {hasActive && <span className="ce-nav-group-active-dot" style={{ background: group.accentColor }} />}
                  <span className={`ce-nav-group-chevron${isCollapsed ? '' : ' open'}`}>
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                      <path d="M2.5 3.75L5 6.25L7.5 3.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                </button>

                <div className={`ce-nav-group-items${isCollapsed ? ' collapsed' : ''}`}>
                  <div className="ce-nav-group-items-inner" style={{ '--group-color': group.accentColor }}>
                    {group.items.map(l => <NavItem key={l.to} link={l} />)}
                  </div>
                </div>
              </div>
            );
          })}
        </nav>

        {/* ── Footer: strategy micro-links only ── */}
        <div className="ce-sidebar-footer">
          <div className="ce-strategy-links">
            {STRATEGY_LINKS.map(l => (
              <Link key={l.to} to={l.to} className="ce-strategy-link" onClick={() => setMobileOpen(false)}>
                {l.key ? t(l.key) : l.label}
              </Link>
            ))}
          </div>
        </div>
      </aside>
    </>
  );
}
